import React from 'react';
import cx from 'classnames';
import { makeStyles } from '@material-ui/styles';
import { useAutomaticFontSizing } from '../lib/useAutomaticFontSizing';
import useDebounce from '../lib/useDebounce';
import { BlockTypes } from '../common/constants';
import { colors } from '@skyslope/mache';

const useStyles = makeStyles({
  wrapper: {
    fontFamily: 'courier',
    fontWeight: 'bold',
    backgroundColor: 'transparent',
    overflow: 'hidden',
    border: `1px solid ${colors.blue[800]}`,
    borderRadius: '2px',
    boxShadow:
      'inset 0 -1.5px 0 0 #E1E7ED, 0 6px 10px 0 rgba(0,32,64,0.07), 0 1px 18px 0 rgba(0,32,64,0.06), 0 3px 5px -1px rgba(0,32,64,0.07)',
    transition: 'border-color 0.1s',
    padding: 1,
    resize: 'none',
    boxSizing: 'border-box',
    wordBreak: 'break-word',
    whiteSpace: 'break-spaces',
    textAlign: 'left',
    lineHeight: 1,
    '&:hover, &:focus': {
      boxShadow:
        'inset 0 -1.5px 0 0 #DCEEFF, 0 6px 10px 0 rgba(0,89,218,0.07), 0 1px 18px 0 rgba(0,89,218,0.06), 0 3px 5px -1px rgba(0,89,218,0.07)',
    },
    '&:focus': {
      outline: 'none',
    },
  },
  focused: {
    border: '2px solid #D20004',
  },
});

interface IProps {
  blockId: string;
  value?: string;
  fontSize: number;
  height: number;
  width: number;
  updateBlock: Function;
  signingExperienceComplete: boolean;
  required: boolean;
  focused: boolean;
  tabIndex: number;
  tabToBlock: (tabIndex: number) => void;
  pageDimensions: { width?: number; height?: number };
  pageScale: number;
}

const TextBox = (props: IProps) => {
  const classes = useStyles();
  const inputRef = React.useRef<HTMLTextAreaElement>(null);

  const { fontSize, value, setValue } = useAutomaticFontSizing({
    height: props.height,
    width: props.width,
    initialFontSize: props.fontSize,
    initialValue: props.value,
    fontFamily: 'courier',
    multiline: true,
    blockType: 'Text',
  });

  const debouncedFontsize = useDebounce(fontSize, 200);
  const debouncedValue = useDebounce(value, 200);

  React.useEffect(() => {
    props.updateBlock({ fontSize, value, blockType: BlockTypes.TEXT });
  }, [debouncedFontsize, debouncedValue]);

  React.useEffect(() => {
    setValue(props.value || '');
  }, [props.blockId]);

  const onType = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value);
  };

  const preventEnter = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.keyCode === 13) {
      e.preventDefault();
    }
  };

  const handleFocus = (e: any) => {
    props.tabToBlock(props.tabIndex);
  };

  return (
    <textarea
      ref={inputRef}
      className={
        props.signingExperienceComplete && value === '' && props.required && props.focused
          ? cx(classes.wrapper, classes.focused)
          : classes.wrapper
      }
      onChange={onType}
      onKeyDown={preventEnter}
      onMouseDown={handleFocus}
      style={{
        height: `100%`,
        width: `100%`,
        fontSize: `${fontSize * props.pageScale}px`,
      }}
      value={value}
    />
  );
};

export default TextBox;
